import React from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  BookOpen,
  Building2,
  Briefcase,
  Calendar,
  DollarSign,
  FileText,
  Link2,
  ListChecks,
  Mail,
  Phone,
  StickyNote,
  Tag,
  User,
} from 'lucide-react';
import AdditionalCounterpartiesEditor from '../../components/AdditionalCounterpartiesEditor';
import ContractPicker from '../../components/ContractPicker';
import { FormCombobox, FormInput, FormSection, FormSelect, FormTextarea, TagInput } from '../../components/FormComponents';
import { PARENT_RELATIONSHIP_OPTIONS, inferParentRelationshipType } from '../../constants/relationships';
import { CONTRACT_STATUSES as STATUSES } from '../../constants/statuses';
import {
  CONFIDENTIALITY_STRUCTURE_OPTIONS as CONFIDENTIALITY_STRUCTURES,
  CONTRACT_CATEGORY_OPTIONS,
  DRAFT_SOURCE_OPTIONS as DRAFT_SOURCES,
  LIGHTSPARK_ENTITY_OPTIONS as LIGHTSPARK_ENTITIES,
  PAYMENT_TERM_OPTIONS as PAYMENT_TERMS,
  RENEWAL_TYPE_OPTIONS as RENEWAL_TYPES,
} from '../../constants/contracts';
import CounterpartySuggestionCallout from './CounterpartySuggestionCallout';
import { useAuth } from '../../context/AuthContext';

export function ContractBasicInformationSection({
  contractTypeOptions,
  errors,
  existingShortNamesForCp,
  formData,
  handleBlur,
  handleChange,
  setFormData,
  touched,
}) {
  const { can } = useAuth();
  return (
    <FormSection title="Basic Information" description="How the contract is classified and who owns it internally." icon={FileText}>
      <div className="form-grid">
        <div className="span-full">
          <FormInput
            label="Contract Title"
            name="title"
            value={formData.title}
            onChange={handleChange}
            onBlur={handleBlur}
            required
            placeholder="Enter a descriptive title..."
            error={touched.title && errors.title}
            icon={FileText}
          />
        </div>
        <FormCombobox
          label="Short Name"
          name="shortName"
          value={formData.shortName}
          onChange={(value) => setFormData((f) => ({ ...f, shortName: value }))}
          options={existingShortNamesForCp.map((deal) => ({
            value: deal.shortName || deal.title,
            label: deal.shortName || deal.title,
            hint: deal.shortName && deal.title && deal.shortName !== deal.title ? deal.title : null,
            meta: deal.status,
          }))}
          placeholder="e.g. Payment Services MSA"
          hint="Drive folder name for this deal"
        />
        <FormSelect
          label="Lightspark Entity"
          name="lightsparkEntity"
          value={formData.lightsparkEntity}
          onChange={handleChange}
          options={LIGHTSPARK_ENTITIES}
          icon={Building2}
        />
        {/* Contract Type + its conditional "specify Other" live in one grid
            cell so revealing the extra field grows this cell downward instead
            of reflowing every field after it. */}
        <div>
          <FormSelect
            label="Contract Type"
            name="contractType"
            value={formData.contractType}
            onChange={handleChange}
            options={contractTypeOptions}
          />
          {formData.contractType === 'Other' && (
            <div style={{ marginTop: 12 }}>
              <FormInput
                label='Contract Type (specify "Other")'
                name="contractTypeOther"
                value={formData.contractTypeOther}
                onChange={handleChange}
                placeholder="e.g. Services Agreement"
              />
            </div>
          )}
        </div>
        <FormSelect
          label="Category"
          name="category"
          value={formData.category || ''}
          onChange={handleChange}
          options={[{ value: '', label: '—' }, ...CONTRACT_CATEGORY_OPTIONS]}
          hint="Optional overarching business category (coexists with contract type)"
        />
        {/* Status + its conditional Draft Source share one cell (same reason). */}
        <div>
          <FormSelect
            label="Status"
            name="status"
            value={formData.status}
            onChange={handleChange}
            options={STATUSES}
          />
          {formData.status !== 'active' && formData.status !== 'expired' && formData.status !== 'terminated' && (
            <div style={{ marginTop: 12 }}>
              <FormSelect
                label="Draft Source"
                name="draftSource"
                value={formData.draftSource || ''}
                onChange={handleChange}
                options={DRAFT_SOURCES}
                hint="Filing hint used in the Drive filename"
              />
            </div>
          )}
        </div>
        <FormInput
          label="Internal Owner"
          name="internalOwner"
          value={formData.internalOwner}
          onChange={handleChange}
          placeholder="Person responsible..."
          icon={User}
        />
        {can('write_contracts') && (
          <div className="span-full">
            <label className="row" style={{ gap: 8, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input
                type="checkbox"
                name="restricted"
                checked={!!formData.restricted}
                onChange={(e) => setFormData((f) => ({ ...f, restricted: e.target.checked }))}
                style={{ marginTop: 2 }}
              />
              <span>
                <span className="field-label" style={{ display: 'block' }}>Restricted (internal only — hidden from the User role)</span>
                <span className="field-hint">
                  Restricted contracts are hidden from base users on list, search, and direct links.
                </span>
              </span>
            </label>
          </div>
        )}
      </div>
    </FormSection>
  );
}

export function ContractCounterpartySection({
  counterpartySuggestions,
  cpFuzzySuggestions,
  formData,
  groupSuggestions,
  setCpFuzzyDismissed,
  setCpFuzzySuggestions,
  setFormData,
}) {
  return (
    <FormSection title="Counterparty" description="The other party to this contract, its aliases, and related entities." icon={Building2}>
      <div className="form-grid">
        <div>
          <FormCombobox
            label="Counterparty"
            name="counterparty"
            value={formData.counterparty}
            onChange={(value) => {
              setCpFuzzyDismissed(false);
              setFormData((f) => ({ ...f, counterparty: value }));
            }}
            onSelect={() => setCpFuzzyDismissed(true)}
            options={counterpartySuggestions}
            placeholder="Company or person name..."
            hint="Start typing to pick an existing counterparty, or enter a new one."
          />
          <CounterpartySuggestionCallout
            suggestions={cpFuzzySuggestions}
            onUse={(suggestion) => {
              setFormData((f) => ({
                ...f,
                counterparty: suggestion.name,
                counterpartyGroup: f.counterpartyGroup || suggestion.group || '',
              }));
              setCpFuzzySuggestions([]);
              setCpFuzzyDismissed(true);
            }}
            onDismiss={() => setCpFuzzyDismissed(true)}
          />
        </div>
        <FormCombobox
          label="Counterparty Group"
          name="counterpartyGroup"
          value={formData.counterpartyGroup}
          onChange={(value) => setFormData((f) => ({ ...f, counterpartyGroup: value }))}
          options={groupSuggestions.map((group) => ({
            value: group.name,
            label: group.name,
            meta: `${group.count} contract${group.count === 1 ? '' : 's'}`,
          }))}
          placeholder="e.g. Fidelity, Bitso - optional"
          emptyHint={
            (formData.counterpartyGroup || '').trim()
              ? 'No match - this will create a new group.'
              : null
          }
          hint="Loose grouping for related entities (e.g. all Fidelity subsidiaries). Leave blank if not part of a family."
        />
        <div className="span-full">
          <label className="field-label">Also known as</label>
          <TagInput
            tags={formData.counterpartyAliases || []}
            onAddTag={(t) => setFormData((f) => ({ ...f, counterpartyAliases: [...(f.counterpartyAliases || []), t] }))}
            onRemoveTag={(t) => setFormData((f) => ({ ...f, counterpartyAliases: (f.counterpartyAliases || []).filter((x) => x !== t) }))}
          />
          <div className="field-hint" style={{ marginTop: 6 }}>
            Alternate, former, or common names for this counterparty (e.g. "Google" for Alphabet Inc.). Matched in search too.
          </div>
        </div>
        <div className="span-full">
          <AdditionalCounterpartiesEditor
            value={formData.additionalCounterparties}
            onChange={(additionalCounterparties) => setFormData((f) => ({ ...f, additionalCounterparties }))}
            counterpartyOptions={counterpartySuggestions}
          />
        </div>
      </div>
    </FormSection>
  );
}

export function ContractRelationshipsSection({
  formData,
  handleChange,
  id,
  parentContract,
  setFormData,
  setParentContract,
}) {
  return (
    <FormSection title="Parent contract" description="Link this to a master agreement if it sits under one (e.g. an SOW under an MSA)." icon={Link2}>
      <div style={{ display: 'grid', gap: 16 }}>
        <ContractPicker
          label={formData.contractType === 'Amendment' ? 'Parent Contract (required for amendments)' : 'Parent Contract (optional)'}
          value={parentContract}
          excludeId={id}
          onChange={(picked) => {
            setParentContract(picked);
            setFormData((prev) => ({
              ...prev,
              parentContractId: picked ? picked.id : null,
              parentRelationshipType: picked
                ? (prev.parentRelationshipType || inferParentRelationshipType(prev))
                : '',
            }));
          }}
          placeholder="Link this to a master agreement..."
        />
        {parentContract && (
          <FormSelect
            label="Relationship to parent"
            name="parentRelationshipType"
            value={formData.parentRelationshipType || 'issued_under'}
            onChange={handleChange}
            options={PARENT_RELATIONSHIP_OPTIONS}
            hint="Controls how this link is labeled in the Related tab."
          />
        )}
      </div>
    </FormSection>
  );
}

/* Micro-label heading for the sub-blocks inside Summary & Flags — mirrors the
   uppercase 10px labels on the contract detail page (Effective / Expires). */
function SubBlockLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 10,
        textTransform: 'uppercase',
        letterSpacing: 0.8,
        color: 'var(--text-tertiary)',
        marginBottom: 12,
      }}
    >
      {children}
    </div>
  );
}

/* Read-only digest of the four flag lists. Editing lives on the detail
   page's Key terms card (inline add/edit/delete per item, next to
   Regenerate/Clear) — the form used to duplicate ~1,400px of that editing
   surface here. Tones match KEY_TERM_TABS so the same concept carries the
   same color across edit and view surfaces. */
const FLAG_DIGEST_ROWS = [
  { key: 'keyRisks', icon: AlertTriangle, tone: 'yellow', label: 'Key risks' },
  { key: 'keyCommitments', icon: Briefcase, tone: 'blue', label: 'Commitments' },
  { key: 'financialTerms', icon: DollarSign, tone: 'green', label: 'Financial terms' },
  { key: 'openIssues', icon: ListChecks, tone: 'purple', label: 'Open items' },
];

export function ContractSummaryFlagsSection({
  formData,
  handleChange,
  id,
}) {
  return (
    <FormSection title="Summary & Flags" description="Plain-English overview, key risks, and open items." icon={FileText}>
      <div style={{ display: 'grid', gap: 20 }}>
        <div>
          <SubBlockLabel>Overview</SubBlockLabel>
          <FormTextarea
            label="Summary"
            name="summary"
            value={formData.summary}
            onChange={handleChange}
            rows={6}
            placeholder="4-6 sentence plain-English summary of what the contract does, commercial structure, material risk allocation, and anything unusual."
            hint="A short, plain-English overview — this is what shows in the AI summary card on the contract page. The longer, formatted detailed summary is edited on that page."
          />
          {id && (
            <Link to={`/contracts/${id}`} className="btn outline xs" style={{ marginTop: 12 }}>
              <BookOpen className="w-3 h-3" />
              Edit detailed summary on the contract page →
            </Link>
          )}
        </div>
        {/* Flag digest — counts only; hidden in create mode (no detail page
            exists yet, and flags are AI-seeded on upload/scan anyway). */}
        {id && (
          <div style={{ borderTop: 'var(--stroke-xs) solid var(--border-primary)', paddingTop: 16 }}>
            <SubBlockLabel>Flags</SubBlockLabel>
            <div>
              {FLAG_DIGEST_ROWS.map(({ key, icon: Icon, tone, label }, index) => {
                const count = Array.isArray(formData[key]) ? formData[key].length : 0;
                return (
                  <div
                    key={key}
                    className="row"
                    style={{
                      gap: 8,
                      padding: '8px 0',
                      borderBottom: index === FLAG_DIGEST_ROWS.length - 1
                        ? 0
                        : 'var(--stroke-xs) solid var(--border-primary)',
                    }}
                  >
                    <Icon className="w-3.5 h-3.5" style={{ color: `var(--text-${tone})`, flexShrink: 0 }} />
                    <span style={{ fontSize: 13 }}>{label}</span>
                    <span className={`chip ${count > 0 ? tone : 'gray'}`} style={{ fontSize: 10 }}>{count}</span>
                    <span style={{ flex: 1 }} />
                    <Link to={`/contracts/${id}`} className="btn ghost xs">
                      Edit on contract page →
                    </Link>
                  </div>
                );
              })}
            </div>
            <div className="field-hint" style={{ marginTop: 8 }}>
              Flag lists are edited on the contract page's Key terms card, next to Regenerate and Clear.
            </div>
          </div>
        )}
      </div>
    </FormSection>
  );
}

export function ContractConfidentialitySection({
  formData,
  handleChange,
}) {
  return (
    <FormSection title="Confidentiality Structure" description="Whether confidentiality runs mutually or one-way." icon={FileText}>
      <div className="form-grid">
        <FormSelect
          label="Confidentiality Structure"
          name="confidentialityStructure"
          value={formData.confidentialityStructure || ''}
          onChange={handleChange}
          options={CONFIDENTIALITY_STRUCTURES}
          hint="Mutual = both parties protect each other's info equally. One-way = only one party's info is protected."
        />
        {formData.confidentialityStructure === 'one_way' && (
          <FormInput
            label="Disclosing Party"
            name="confidentialityDisclosingParty"
            value={formData.confidentialityDisclosingParty || ''}
            onChange={handleChange}
            placeholder="Full legal name of the party whose info is protected"
            icon={Building2}
            hint="Only populated when structure is one-way."
          />
        )}
      </div>
    </FormSection>
  );
}

export function ContractDatesTermsSection({
  formData,
  handleChange,
  expirationHint,
}) {
  return (
    <FormSection title="Dates & Terms" description="Effective, executed, and expiration dates, renewal, and notice windows." icon={Calendar}>
      <div className="form-grid cols-3">
        <FormInput
          label="Effective Date"
          name="effectiveDate"
          type="date"
          value={formData.effectiveDate}
          onChange={handleChange}
          icon={Calendar}
        />
        <FormInput
          label="Executed Date"
          name="executedDate"
          type="date"
          value={formData.executedDate}
          onChange={handleChange}
          hint="When the contract was signed"
          icon={Calendar}
        />
        <FormInput
          label="Expiration Date"
          name="expirationDate"
          type="date"
          value={formData.expirationDate}
          onChange={handleChange}
          hint={expirationHint}
          icon={Calendar}
        />
        <FormSelect
          label="Renewal Type"
          name="renewalType"
          value={formData.renewalType}
          onChange={handleChange}
          options={RENEWAL_TYPES}
        />
        <FormInput
          label="Renewal Notice Days"
          name="renewalNoticeDays"
          type="number"
          value={formData.renewalNoticeDays}
          onChange={handleChange}
          min="0"
        />
        <FormInput
          label="Termination Notice Days"
          name="terminationNoticeDays"
          type="number"
          value={formData.terminationNoticeDays}
          onChange={handleChange}
          min="0"
        />
      </div>
    </FormSection>
  );
}

export function ContractFinancialTermsSection({
  formData,
  handleChange,
}) {
  return (
    <FormSection title="Financial Terms" description="Contract value and payment terms." icon={DollarSign}>
      <div className="form-grid">
        <FormInput
          label="Contract Value"
          name="contractValue"
          type="number"
          value={formData.contractValue}
          onChange={handleChange}
          min="0"
          step="0.01"
          placeholder="0.00"
          icon={DollarSign}
        />
        <FormSelect
          label="Payment Terms"
          name="paymentTerms"
          value={formData.paymentTerms}
          onChange={handleChange}
          options={PAYMENT_TERMS}
        />
      </div>
    </FormSection>
  );
}

export function ContractContactSection({
  formData,
  handleChange,
}) {
  return (
    <FormSection title="Contact" description="Primary point of contact at the counterparty." icon={User}>
      <div className="form-grid">
        <FormInput
          label="Contact Name"
          name="counterpartyContactName"
          value={formData.counterpartyContactName}
          onChange={handleChange}
          placeholder="Jane Doe"
          icon={User}
        />
        <FormInput
          label="Contact Email"
          name="counterpartyContactEmail"
          type="email"
          value={formData.counterpartyContactEmail}
          onChange={handleChange}
          placeholder="jane@example.com"
          icon={Mail}
        />
        <FormInput
          label="Contact Phone"
          name="counterpartyContactPhone"
          type="tel"
          value={formData.counterpartyContactPhone}
          onChange={handleChange}
          placeholder="+1 415 555 0100"
          icon={Phone}
        />
        <FormInput
          label="Contact Role"
          name="counterpartyContactRole"
          value={formData.counterpartyContactRole}
          onChange={handleChange}
          placeholder="General Counsel"
          icon={Briefcase}
        />
      </div>
    </FormSection>
  );
}

export function ContractTagsSection({
  formData,
  handleAddTag,
  handleRemoveTag,
  label = 'Contract Tags',
  options = [],
  hint = 'Press Enter to add a tag, Backspace to remove.',
}) {
  return (
    <FormSection title="Tags" icon={Tag}>
      <div>
        <label className="field-label">{label}</label>
        <TagInput
          tags={formData.tags}
          onAddTag={handleAddTag}
          onRemoveTag={handleRemoveTag}
          options={options}
        />
        {hint && (
          <div className="field-hint" style={{ marginTop: 6 }}>
            {hint}
          </div>
        )}
      </div>
    </FormSection>
  );
}

export function ContractNotesSection({
  formData,
  handleChange,
  icon = StickyNote,
  placeholder = 'Add any additional notes...',
}) {
  return (
    <FormSection title="Notes" icon={icon || FileText}>
      <FormTextarea
        label="Additional Notes"
        name="notes"
        value={formData.notes}
        onChange={handleChange}
        rows={4}
        placeholder={placeholder}
      />
    </FormSection>
  );
}
